import React, {useEffect, useState} from 'react';
import parse from "html-react-parser";
import { addYears, addDays, parseISO } from 'date-fns';
import {Link} from "react-router-dom";

import Modal from '../../../Layer/Modal/Modal';
import TextField, { Type } from '../../../../Component/Input/TextField/TextField';
import Select from '../../../../Component/Input/SelectInput/SelectInput';
import DatePicker from "../../../../Component/Input/DatePicker/DatePicker";
import {TimeOffTypeIconsCollection} from "../../../../../shared/TimeOffType/constants/TimeOffTypeIconsCollection";

import {SelectValue} from '../../../../../shared/Common/data/SelectValue';
import {EmployeeFromApi} from "../../../../../shared/Employee/data/EmployeeFromApi";
import {TimeOffTypeFromApi} from "../../../../../shared/TimeOffType/data/TimeOffTypeFromApi";
import {IconFromApi} from "../../../../../shared/TimeOffType/data/IconFromApi";
import {RequestFromApi} from "../../../../../shared/Calendar/Request/data/RequestFromApi";
import {TimeOffAccruedDayFromApi} from "../../../../../shared/TimeOffAccruedDay/data/TimeOffAcruedDayFromApi";
import {AuthEmployee} from "../../../../../shared/Security/data/AuthEmployee";

import './BaseModal.scss';

import {
    CALENDAR,
    EMPLOYEE,
    EMPLOYEES,
    HOLIDAYS,
    TIME_OFF_TYPE_ICONS,
    TIME_OFF_TYPES
} from '../../../../../utils/constants/api';
import {ADMIN_TIMEOFF_TYPES} from "../../../../../utils/constants/routes";
import {VALIDATION_ERROR} from "../../../../../utils/constants/errors";
import {ROLE_ADMIN, ROLE_EMPLOYEE, ROLE_HR} from "../../../../../shared/Security/constants/AccessLevels";
import {NEW, APPROVED, CANCELLED, REJECTED} from "../../../../../shared/Calendar/Request/constants/Statuses";

import { ValidationError } from '../../../../../shared/Common/Error/ValidationError';
import moment from "moment";
import {Tooltip as ReactTooltip} from "react-tooltip";
import {dateFromStringWithDefaultTimezone} from "../../../../../utils/helpers/DateTime/dateFromStringWithDefaultTimezone";

type BaseModal = {
    opened: boolean,
    modalTitle: string,
    okText: string,
    requester?: EmployeeFromApi | null,
    onCancel: () => any,
    request: RequestFromApi | null,
    requests?: RequestFromApi[] | null,
    onChangeStatus?: (request: RequestFromApi[]) => void
    removeFooter?: boolean
    authEmployee: AuthEmployee,
    onOk: (
        requester: string,
        type: string,
        startDate: Date | null,
        endDate: Date | null,
        substitute: string,
        balance: string,
        comment: string,
        validate: (errors: ValidationError[]) => void
    ) => void
    checkConfirmRequest?: (request: RequestFromApi) => void,
    origin? : string,
};

function BaseModal(props: BaseModal) {
    const emptySelect = { label: '', value: 'empty' } as SelectValue;

    const [requester, setRequester] = useState<SelectValue>(
        props.request
            ? { value: props.request.requesterId, label: props.request?.requesterNickname } as SelectValue
            : emptySelect
    );

    const [balance, setBalance] = useState<SelectValue>(
        props.request
            ? props.request.policyId
                ? {label: props.request?.policyName, value: props.request.policyId} as SelectValue
                : {label: 'Unpaid Time Off', value: ''} as SelectValue
            : emptySelect
    );

    const [approverRelatedPolicies, setApproverRelatedPolicies] = useState<string[]>([]);
    const [balances, setBalances] = useState<TimeOffAccruedDayFromApi[] | null>(null);
    const [type, setType] = useState<string>(props.request ? props.request.typeId : '');
    const [startDate, setStartDate] = useState<Date | null>(props.request ? dateFromStringWithDefaultTimezone(props.request.startDate.slice(0, 10)) : null);
    const [endDate, setEndDate] = useState<Date | null>(props.request ? dateFromStringWithDefaultTimezone(props.request.endDate.slice(0, 10)) : null);
    const [maxEndDate, setMaxEndDate] = useState<Date>(addYears(new Date(), 3));
    const [lastSalaryDates, setLastSalaryDates] = useState<string[]>([]);
    const [minStartDate, setMinStartDate] = useState<Date>(calculateMinDate);
    const [substitute, setSubstitute] = useState<SelectValue>(props.request
        ? { label: props.request.substituteNickname, value: props.request.substituteId } as SelectValue
        :  emptySelect);
    const [comment, setComment] = useState<string>(props.request ? props.request.comment : '');
    const [timeOffDays, setTimeOffDays] = useState<[]>([]);
    const [disabledDates, setDisabledDates] = useState<Date[]>([]);
    const [substituteDates, setSubstituteDates] = useState<Date[]>([]);

    const [requesterOptions, setRequesterOptions] = useState<SelectValue[]>([]);
    const [balanceOptions, setBalanceOptions] = useState<SelectValue[]>([]);
    const [substituteOptions, setSubstituteOptions] = useState<SelectValue[]>([]);

    const [requesterError, setRequesterError] = useState<string | null>(null);
    const [balanceError, setBalanceError] = useState<string | null>(null);
    const [typeError, setTypeError] = useState<string | null>(null);
    const [startDateError, setStartDateError] = useState<string | null>(null);
    const [endDateError, setEndDateError] = useState<string | null>(null);
    const [substituteError, setSubstituteError] = useState<string | null>(null);
    const [commentError, setCommentError] = useState<string | null>(null);

    const [iconsFromApi, setIconsFromApi] = useState<IconFromApi[]>([]);
    const [timeOffTypes, setTimeOffTypes] = useState<TimeOffTypeFromApi[]>([]);
    const [status, setStatusRequest] = useState<string>(props.request ? props.request.status : '');
    const [selectedStep, setSelectedStep] = useState<number | null>(null);

    const hasRoleAdmin = props.authEmployee.roles.includes(ROLE_ADMIN);
    const hasRoleHr = props.authEmployee.roles.includes(ROLE_HR);
    const hasRoleEmployee = props.authEmployee.roles.includes(ROLE_EMPLOYEE);

    const [hierarchyChainIds, setHierarchyChainIds] = useState<string[]>([]);
    const [clearDaysCount, setClearDaysCount] = useState<number>(0);

    useEffect(() => {
            getDayOffDatesByPeriodAndEmployee(calculateMinDate());
            getSubstituteDatesByPeriodAndEmployee(calculateMinDate());
            getBalances();
            if (props.modalTitle !== 'Preview Request') {
                resetAllValues(false);
                resetAllErrors();
            }
    }, [requester]);

    useEffect(() => {
        if (props.authEmployee && props.authEmployee.id) {
            getHierarchyChainIds(props.authEmployee.id)
                .then((ids: string[]) => setHierarchyChainIds(ids));
        }
    }, [props.authEmployee]);

    useEffect(() => {
        if(startDate && endDate && requester){
            getSubstitute(formatDate(startDate), formatDate(endDate));
        }
    }, [startDate, endDate, requester]);

    useEffect(() => {
        getLastSalaryDates()
        getApproverRelatedPolicies()
        getRequesters()
        getTimeOffTypes()
        getIconsFromApi()
    }, []);

    useEffect(() => {
        setMinStartDate(startMinDate());
    }, [timeOffDays]);

    useEffect(() => {
        if (props.opened) {
            if (props.requester) {
                setRequester({value: props.requester?.id, label:props.requester?.nickname} as SelectValue)
            }
            if (!hasRoleHr && !hasRoleAdmin && hasRoleEmployee && !props.requester) {
                setRequester({ value: props.authEmployee.id, label: props.authEmployee.nickname } as SelectValue);
            }
        }
    }, [props.opened]);

    useEffect(() => {
        if (endDate && startDate) {
            if (startDate > endDate) {
                setEndDate(startDate);
            }
        }
    }, [startDate]);

    useEffect(() => {
        if (balances && balance && balance.value !== 'empty') {
            if (startDate) {
                let balanceItem = balances.find(item => item.policyId === balance.value);
                if (balanceItem) {
                    let newEndDate = startDate;
                    let balanceQuantity = balanceItem.quantity;
                    for (let i = 1; i <= balanceQuantity; i++) {
                        if (i !== 1) {
                            newEndDate = addDays(newEndDate, 1);
                        }
                        for (let j = 0; j < timeOffDays.length; j++) {
                            let dateString = formatDate(newEndDate);
                            if (timeOffDays[j] === dateString) {
                                balanceQuantity++;
                            }
                        }
                    }

                    setMaxEndDate(addDays(startDate, balanceQuantity - 1));
                    if (endDate) {
                        if (endDate > addDays(startDate, balanceQuantity - 1)) {
                            let newEndDate = addDays(startDate, balanceQuantity - 1);
                            while (timeOffDays.some(date => date === formatDate(newEndDate))) {
                                newEndDate = addDays(newEndDate, -1);
                            }
                            setEndDate(newEndDate);
                        }
                    }
                } else {
                    setMaxEndDate(addYears(new Date(), 3));
                }
            } else if (endDate) {
                setEndDate(null);
            }
        }
    }, [balance, startDate]);

    useEffect(() => {
        if (endDate && startDate) {
            setClearDaysCount(getClearDaysCount(startDate, endDate, disabledDates));
        }
    }, [startDate, endDate, disabledDates]);

    function getRequesters() {
        fetch(EMPLOYEES + '/type/active', {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then(async (employees: EmployeeFromApi[]) => {
                let options = employees.map(employee => {
                    return {
                        value: employee.id,
                        label: employee.nickname
                    }
                });

                if (!hasRoleAdmin && !hasRoleHr && hasRoleEmployee) {
                    let hierarchyChainIds: string[] = await getHierarchyChainIds(props.authEmployee.id);
                    options = options.filter(
                        option => option.value === props.authEmployee.id ||
                            hierarchyChainIds.includes(option.value)
                    );
                }

                setRequesterOptions(options);
            });
    }

    function getHierarchyChainIds(employeeId: string): Promise<string[]> {
        return fetch(EMPLOYEE + '/' + employeeId + '/all-hierarchy-chains', {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json());
    }

    function isHierarchyChain(employeeId: string): boolean {
        return hierarchyChainIds.includes(employeeId);
    }

    function getSubstitute(dateStart: string, dateEnd: string) {
        return fetch(EMPLOYEES + '/type/active-without-request-for-period?date_start=' + dateStart + '&date_end=' + dateEnd, {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then((employees: EmployeeFromApi[]) => {
                let requesterInfo = employees.find(employee => employee.id === requester.value);
                const options = employees
                    .filter(employee => employee.id !== requester.value)
                    .map(employee => ({
                        value: employee.id,
                        label: employee.nickname
                    }));

                setSubstituteOptions(options)
                if (options && props.request != null) {
                    setSubstitute(options.find(option => option.value === props.request?.substituteId) as SelectValue);
                }
            });
    }

    function getBalances() {
        if (requester.value && requester.value !== 'empty') {
            return fetch(EMPLOYEE + '/' +  requester.value + '/time-off-accrued-days/', {
                method: 'GET',
                headers: {'Accept': 'application/json', 'Content-type': 'application/json'}
            })
                .then(response => response.json())
                .then((timeOffAccruedDays: TimeOffAccruedDayFromApi[]) => {
                    setBalances(timeOffAccruedDays);
                    let needWorkingTime = true;
                    const options = timeOffAccruedDays.map(timeOffAccruedDay => {
                        if (needWorkingTime && timeOffAccruedDay.maxQuantity && timeOffAccruedDay.quantity >= timeOffAccruedDay.maxQuantity) {
                            needWorkingTime = false
                        }
                        return {
                            value: timeOffAccruedDay.policyId,
                            label: parse('<div class="available-block"><span>' + timeOffAccruedDay.policyName + '</span><span>Available ' + timeOffAccruedDay.quantity + ' days</span></div>') as string
                        }
                    });
                    let balanceOptions = options;
                    if (needWorkingTime || balanceOptions.length < 1) {
                        balanceOptions = [{label: 'Unpaid Time Off', value: ''}].concat(options);
                    }
                    setBalanceOptions(balanceOptions)
                });
        }
    }

    function getDayOffDatesByPeriodAndEmployee(startDate: Date) {
        if (requester.value && requester.value !== 'empty') {
            const endDate = addDays(startDate, 720)
            let startPeriod = startDate.getFullYear() + '-' + (startDate.getMonth() + 1) + "-" + startDate.getDate();
            let endPeriod = endDate.getFullYear() + '-' + (endDate.getMonth() + 1) + "-" + endDate.getDate();
            const withRequestsValue = props.request ? 'false' : 'true';
            fetch(HOLIDAYS + '/employee/' + requester.value + '/' + startPeriod + '/' + endPeriod +
                '?withRequests=' + withRequestsValue, {
                method: 'GET',
                headers: {'Accept': 'application/json', 'Content-type': 'application/json'}
            })
                .then(response => response.json())
                .then((timeOffDates: []) => {
                    setTimeOffDays(timeOffDates)
                    setDisabledDates(timeOffDates.map(value => {
                        return parseISO(value);
                    }))
                });
        }
    }

    function getSubstituteDatesByPeriodAndEmployee(startDate: Date) {
        if (requester.value && requester.value !== 'empty') {
            const endDate = addDays(startDate, 720)
            let startPeriod = startDate.getFullYear() + '-' + (startDate.getMonth() + 1) + "-" + startDate.getDate();
            let endPeriod = endDate.getFullYear() + '-' + (endDate.getMonth() + 1) + "-" + endDate.getDate();
            fetch(EMPLOYEES + '/' + requester.value + '/substitute-request-dates?start-date=' + startPeriod + '&end-date=' +endPeriod, {
                method: 'GET',
                headers: {'Accept': 'application/json', 'Content-type': 'application/json'}
            })
                .then(response => response.json())
                .then((dates: []) => {
                    setSubstituteDates(dates.map(value => {
                        return new Date(value);
                    }))
                });
        }
    }

    function getTimeOffTypes() {
        fetch(TIME_OFF_TYPES, {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then((timeOffTypes: TimeOffTypeFromApi[]) => setTimeOffTypes(timeOffTypes));
    }

    function getIconsFromApi(): void {
        fetch(TIME_OFF_TYPE_ICONS, {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then((icons: IconFromApi[]) => setIconsFromApi(icons))
    }

    function getIconNameById(id: number): string {
        let iconName = iconsFromApi.find(icon => icon.id === id)?.correspondingIconName;
        return iconName ? iconName : '';
    }

    function formatDate(date: Date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function resetAllValues(withRequester: boolean = true): void {
        if (props.origin !== 'profile' && withRequester) {
            setRequester(emptySelect);
        }
        setComment('');
        setType('');
        setStartDate(null);
        setSubstitute(emptySelect);
        setEndDate(null);
        setBalance(emptySelect);
        setMaxEndDate(addYears(new Date(), 3));
        setSubstituteOptions([]);
        setSelectedStep(null);
    }

    function resetAllErrors(): void {
        setCommentError(null);
        setTypeError(null);
        setEndDateError(null);
        setStartDateError(null);
        setSubstituteError(null);
        setRequesterError(null);
        setBalanceError(null);
    }

    function validate(errors: ValidationError[]): void {
        if (errors.length < 1 && !props.request) {
            resetAllValues()
        }
        errors.forEach((error: ValidationError) => {
            if (error.property === 'comment') {
                setCommentError(error.message);
            }
            if (error.property === 'type') {
                setTypeError(error.message);
            }
            if (error.property === 'startDate') {
                setStartDateError(error.message);
            }
            if (error.property === 'endDate') {
                setEndDateError(error.message);
            }
            if (error.property === 'substitute') {
                setSubstituteError(error.message);
            }
            if (error.property === 'requester') {
                setRequesterError(error.message);
            }
            if (error.property === 'balance') {
                setBalanceError(error.message);
            }
        });
    }

    function frontValidate(): boolean {
        if (requester.value === 'empty') {
            setRequesterError('Please select requester');

            return false;
        }

        return true;
    }

    function setStatus(requestId: string, status: string) {
        setStatusRequest(status);
        fetch(CALENDAR + '/requests/' + requestId + '/status/' + status + '?status-change-by=' + props.authEmployee.id, {
            method: 'PUT',
        }).then(response => {
            if (response.ok && props.requests) {
                const updatedRequests = props.requests.map(request => {
                    if (request.id === requestId) {
                        return {
                            ...request,
                            status: status,
                            statusChangeByNickname: props.authEmployee.nickname
                        };
                    }
                    return request;
                });
                if (props.onChangeStatus) {
                    props.onChangeStatus(updatedRequests);
                }
            } else if (response.status === VALIDATION_ERROR) {
                response.json().then((errors: ValidationError[]) => validate(errors));
            }
            getBalances()
            props.onOk(requester.value, type, startDate, endDate, substitute ? substitute.value : '', balance.value, comment, validate)
        });
    }

    function getApproverRelatedPolicies() {
        return fetch(EMPLOYEES + '/' + props.authEmployee.id + '/approver-related-policies', {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then((policies: string[]) => setApproverRelatedPolicies(policies));
    }

    function getLastSalaryDates() {
        fetch(EMPLOYEES + '/salary/last-dates', {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then((allLastSalaryDates: string[]) => {
                setLastSalaryDates(allLastSalaryDates)
            })
    }

    function calculateMinDate() {
        let today = new Date();
        let year = today.getFullYear() - 1;
        let oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(year);

        return oneYearAgo;
    }

    function startMinDate() {
        // @ts-ignore
        let date = lastSalaryDates[requester.value] !== undefined ? new Date(lastSalaryDates[requester.value].lastDateSalary.substring(0, 10)) : calculateMinDate();
        date.setDate(date.getDate() + 1);
        if (timeOffDays) {
            for (let j = 0; j < timeOffDays.length; j++) {
                let dateString = formatDate(date);
                if (timeOffDays[j] === dateString) {
                    date.setDate(date.getDate() + 1);
                }
            }
        }

        return date;
    }

    function handleDateOnClick() {
        if (requester.value === 'empty') {
            setRequesterError('Please select the requester first.');
        }
    }

    function getClearDaysCount(startDate: Date, endDate: Date, disabledDates: Date[]): number {
        let clearDays = 0;
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            if (!isDateInArray(currentDate, disabledDates)) {
                clearDays++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return clearDays;
    }

    function isDateInArray(date: Date, datesArray: Date[]): boolean {
        return datesArray.some(d =>
            d.getFullYear() === date.getFullYear() &&
            d.getMonth() === date.getMonth() &&
            d.getDate() === date.getDate()
        );
    }

    return (
        <div className={'request-modal'}>
            <Modal
                opened={ props.opened }
                modalTitle={ props.modalTitle }
                cancelText='Close'
                okText={ props.okText }
                onCancel={() => {
                    props.onCancel();
                    resetAllValues();
                    resetAllErrors();
                }}
                onOk={() => {
                    if (frontValidate()) {
                        props.onOk(requester.value, type, startDate, endDate, substitute ? substitute.value : '', balance.value, comment, validate);
                        getDayOffDatesByPeriodAndEmployee(calculateMinDate());
                    }
                }}
                removeFooter={props.removeFooter}
            >
                <div className={props.request ? 'modal-inner' : ''}>


                        <div
                            className={`request-step ${(selectedStep === 1 || selectedStep === null) && props.modalTitle !== 'Preview Request' ? 'selected' : ''}`}
                            onClick={() => setSelectedStep(1)}
                        >
                            <p>1. Choose who will take the time-off</p>
                            <Select
                                required={ false }
                                disabled={ !!props.requester }
                                isMulti={ false }
                                value={ requester }
                                options={ requesterOptions }
                                error={ requesterError }
                                onChange={ (newRequester: SelectValue) => {
                                    setRequester(newRequester);
                                    setRequesterError(null);
                                }}
                                isSearchable={ true }
                            />
                        </div>

                    {!props.request || props.request.typeId ?
                        <div
                            className={`request-step ${selectedStep === 2 ? 'selected' : ''}`}
                            onClick={() => setSelectedStep(2)}
                        >
                            <p>2. Choose the icon to display the time-off request</p>
                            {timeOffTypes.length > 0 ?
                                <div className={'custom-radios-block input'}>
                                    {timeOffTypes.map(timeOffType => (
                                        <div onClick={() => {
                                            setType(timeOffType.id);
                                            setTypeError(null)
                                        }} className={type === timeOffType.id ? 'choosen-type' : ''}
                                             key={timeOffType.id}>
                                            {TimeOffTypeIconsCollection[getIconNameById(timeOffType.value.timeOffTypeIconId) as keyof typeof TimeOffTypeIconsCollection] ?
                                                parse(TimeOffTypeIconsCollection[getIconNameById(timeOffType.value.timeOffTypeIconId) as keyof typeof TimeOffTypeIconsCollection])
                                                : ''}
                                            <span>{timeOffType.value.name}</span>
                                        </div>
                                    ))}
                                    {typeError ?
                                        <div className='error'>
                                            <svg width="18" height="17" viewBox="0 0 18 17" fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M9.83317 6.00002H8.1665V4.33335H9.83317M9.83317 12.6667H8.1665V7.66669H9.83317M8.99984 0.166687C7.90549 0.166687 6.82185 0.382235 5.81081 0.801024C4.79976 1.21981 3.8811 1.83364 3.10728 2.60746C1.54448 4.17027 0.666504 6.28988 0.666504 8.50002C0.666504 10.7102 1.54448 12.8298 3.10728 14.3926C3.8811 15.1664 4.79976 15.7802 5.81081 16.199C6.82185 16.6178 7.90549 16.8334 8.99984 16.8334C11.21 16.8334 13.3296 15.9554 14.8924 14.3926C16.4552 12.8298 17.3332 10.7102 17.3332 8.50002C17.3332 7.40567 17.1176 6.32204 16.6988 5.31099C16.28 4.29994 15.6662 3.38128 14.8924 2.60746C14.1186 1.83364 13.1999 1.21981 12.1889 0.801024C11.1778 0.382235 10.0942 0.166687 8.99984 0.166687Z"
                                                    fill="#E05D44"/>
                                            </svg>
                                            <span>{typeError}</span>
                                        </div>
                                        : null}
                                </div> :
                                <div className={'types-link'}><Link to={ADMIN_TIMEOFF_TYPES}>Add at least one Time Off
                                    Type</Link></div>
                            }
                        </div> : null}

                    <div
                        className={`request-step ${selectedStep === 3 ? 'selected' : ''}`}
                        onClick={() => setSelectedStep(3)}
                    >
                        <p>3. Choose the dates of absence
                        <svg id="app-title" className={'tooltip-help'} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.0834 14.854C10.3056 14.854 10.4931 14.7776 10.6459 14.6248C10.7987 14.4721 10.8751 14.2846 10.8751 14.0623C10.8751 13.8401 10.7987 13.6526 10.6459 13.4998C10.4931 13.3471 10.3056 13.2707 10.0834 13.2707C9.86119 13.2707 9.67369 13.3471 9.52091 13.4998C9.36814 13.6526 9.29175 13.8401 9.29175 14.0623C9.29175 14.2846 9.36814 14.4721 9.52091 14.6248C9.67369 14.7776 9.86119 14.854 10.0834 14.854ZM9.35425 11.8123H10.5834C10.5834 11.4512 10.6286 11.1214 10.7188 10.8228C10.8091 10.5241 11.0904 10.1804 11.5626 9.7915C11.9931 9.43039 12.2987 9.07623 12.4792 8.729C12.6598 8.38178 12.7501 7.99984 12.7501 7.58317C12.7501 6.84706 12.5105 6.25678 12.0313 5.81234C11.5522 5.36789 10.9167 5.14567 10.1251 5.14567C9.44452 5.14567 8.84383 5.31581 8.323 5.65609C7.80216 5.99636 7.42369 6.46511 7.18758 7.06234L8.29175 7.479C8.44452 7.09011 8.67369 6.78803 8.97925 6.57275C9.2848 6.35748 9.64591 6.24984 10.0626 6.24984C10.5348 6.24984 10.9167 6.37831 11.2084 6.63525C11.5001 6.8922 11.6459 7.22206 11.6459 7.62484C11.6459 7.93039 11.5556 8.21859 11.3751 8.48942C11.1945 8.76025 10.9306 9.0415 10.5834 9.33317C10.1667 9.69428 9.85772 10.0519 9.65633 10.4061C9.45494 10.7603 9.35425 11.229 9.35425 11.8123ZM10.0001 18.3332C8.86119 18.3332 7.7848 18.1144 6.77091 17.6769C5.75703 17.2394 4.87161 16.6422 4.11466 15.8853C3.35772 15.1283 2.7605 14.2429 2.323 13.229C1.8855 12.2151 1.66675 11.1387 1.66675 9.99984C1.66675 8.84706 1.8855 7.76373 2.323 6.74984C2.7605 5.73595 3.35772 4.854 4.11466 4.104C4.87161 3.354 5.75703 2.76025 6.77091 2.32275C7.7848 1.88525 8.86119 1.6665 10.0001 1.6665C11.1529 1.6665 12.2362 1.88525 13.2501 2.32275C14.264 2.76025 15.1459 3.354 15.8959 4.104C16.6459 4.854 17.2397 5.73595 17.6772 6.74984C18.1147 7.76373 18.3334 8.84706 18.3334 9.99984C18.3334 11.1387 18.1147 12.2151 17.6772 13.229C17.2397 14.2429 16.6459 15.1283 15.8959 15.8853C15.1459 16.6422 14.264 17.2394 13.2501 17.6769C12.2362 18.1144 11.1529 18.3332 10.0001 18.3332ZM10.0001 17.0832C11.9723 17.0832 13.6459 16.3922 15.0209 15.0103C16.3959 13.6283 17.0834 11.9582 17.0834 9.99984C17.0834 8.02761 16.3959 6.354 15.0209 4.979C13.6459 3.604 11.9723 2.9165 10.0001 2.9165C8.04175 2.9165 6.37161 3.604 4.98966 4.979C3.60772 6.354 2.91675 8.02761 2.91675 9.99984C2.91675 11.9582 3.60772 13.6283 4.98966 15.0103C6.37161 16.3922 8.04175 17.0832 10.0001 17.0832Z" fill="#6A7885"/>
                        </svg>
                            <ReactTooltip
                                anchorId="app-title"
                                place="bottom"
                                html={`
                                        <p>Dates highlighted in grey are weekends, holiday or past periods are unavailable for selection.</p>
                                        <p>Dates highlighted in blue indicate that the requester is substituting for the other employee and are available for selection.</p>
                                `}
                                noArrow={true}
                            />
                        </p>
                        <div className={'date-block'}>
                            <DatePicker
                                label={'From'}
                                placeholder={' '}
                                required={false}
                                horizontal={true}
                                date={startDate}
                                error={startDateError}
                                onChange={newDate => {
                                    setStartDate(newDate);
                                    setStartDateError(null);
                                }}
                                maxDate={null}
                                minDate={minStartDate}
                                disabled={requester.value === 'empty'}
                                excludeWeekends={true}
                                disabledDates={disabledDates}
                                onClick={handleDateOnClick}
                                highlightDates={substituteDates}
                            />
                            <i className="material-icons icon-start">calendar_today</i>
                            <DatePicker
                                label={parse('&nbsp;&mdash;&nbsp; To') as string}
                                required={false}
                                placeholder={' '}
                                horizontal={true}
                                date={endDate}
                                error={endDateError}
                                onChange={newDate => {
                                    setEndDate(newDate);
                                    setEndDateError(null);
                                }}
                                maxDate={maxEndDate}
                                minDate={startDate ? startDate : calculateMinDate()}
                                disabled={requester.value === 'empty'}
                                excludeWeekends={true}
                                disabledDates={disabledDates}
                                onClick={handleDateOnClick}
                                highlightDates={substituteDates}
                            />
                            <i className="material-icons icon-end">calendar_today</i>
                        </div>
                        {startDate && endDate && clearDaysCount > 0 &&
                            <div className={'total-working-days'}>
                                <div className={'total-working-days-label'}>
                                    Total:
                                </div>
                                <div className={'total-working-days-label-bold'}>
                                    {clearDaysCount} working days
                                </div>
                            </div>
                        }
                    </div>

                    <div
                        className={`request-step ${selectedStep === 4 ? 'selected' : ''}`}
                        onClick={() => setSelectedStep(4)}
                    >
                        <p>4. Choose which time-off balance to deduct absence from</p>
                        <div className={'balance-field'}>
                            <Select
                                required={false}
                                isMulti={false}
                                value={balance}
                                options={balanceOptions}
                                error={balanceError}
                                onChange={(newBalance: SelectValue) => {
                                    setBalance(newBalance);
                                    setBalanceError(null);
                                }}
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    <div
                        className={`request-step ${selectedStep === 5 ? 'selected' : ''}`}
                        onClick={() => setSelectedStep(5)}
                    >
                        <p>5. Choose who will be covering during the absence</p>
                        <Select
                            required={false}
                            isMulti={false}
                            value={substitute}
                            options={substituteOptions}
                            error={substituteError}
                            onChange={(newSubstitute: SelectValue) => {
                                setSubstitute(newSubstitute);
                                setSubstituteError(null);
                            }}
                            onMenuOpen={() => {
                                if (startDate === null || endDate === null) {
                                    setStartDateError('Please select the dates first');
                                    setEndDateError('Please select the dates first');
                                }
                            }}
                            isSearchable={true}
                        />
                    </div>

                    <div
                        className={`request-step ${selectedStep === 6 ? 'selected' : ''}`}
                        onClick={() => setSelectedStep(6)}
                    >
                        <p>6. Add a comment to the time-off request</p>
                        <TextField
                            placeholder=''
                            required={false}
                            value={comment}
                            type={Type.Textarea}
                            horizontal={true}
                            error={commentError}
                            onChange={newComment => {
                                setComment(newComment);
                                setCommentError(null);
                            }}
                            maxLength={500}
                        />
                    </div>

                </div>
                    {props.request && status !== REJECTED && status !== CANCELLED
                    && (hasRoleAdmin
                        || hasRoleHr
                        || approverRelatedPolicies.includes(props.request.policyId)
                        || (hasRoleEmployee && props.authEmployee.id === props.request.requesterId)
                        || isHierarchyChain(props.request.requesterId)
                    ) ?
                        <div className='request-modal-footer'>
                            <div onClick={() => {
                                props.onCancel()
                            }} className={'close-without-border request-btn'}>Close
                            </div>
                            {(props.request.lastDateSalary === null || moment(props.request.lastDateSalary).toDate() < moment(props.request.startDate).toDate()) &&
                                <>
                                    {status === 'New' && (hasRoleAdmin || hasRoleHr || approverRelatedPolicies.includes(props.request.policyId) || isHierarchyChain(props.request.requesterId)) ?
                                        <div>
                                            <div onClick={() => { // @ts-ignore
                                                setStatus(props.request.id, 'Rejected');
                                            }
                                            } className={'Reject request-btn'}>Reject request
                                            </div>
                                            <div onClick={() => { // @ts-ignore
                                                props.checkConfirmRequest ? props.checkConfirmRequest(props.request) : setStatus(props.request.id, 'Approved');
                                            }
                                            } className={'Approve request-btn'}>Approve request
                                            </div>
                                        </div> : null}
                                    {(status === 'Approved' && (hasRoleAdmin || hasRoleHr || approverRelatedPolicies.includes(props.request.policyId) || isHierarchyChain(props.request.requesterId)))
                                    || (status === 'New' && !hasRoleAdmin && !hasRoleHr && hasRoleEmployee && props.authEmployee.id === props.request.requesterId && !approverRelatedPolicies.includes(props.request.policyId))
                                        ? <div onClick={() => { // @ts-ignore
                                            setStatus(props.request.id, 'Cancelled');
                                        }
                                        } className={'Cancel request-btn'}>Cancel request</div>
                                        : null}
                                </>}
                        </div>
                        : null}
                {(status === CANCELLED || status === REJECTED) ?
                    <div className='request-modal-footer'>
                        <div onClick={() => {
                            props.onCancel()
                        }} className={'close request-btn'}>Close
                        </div>
                    </div>
                    : null}
            </Modal>
        </div>
    );
}

export default BaseModal;